"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isPWAInstalled, isIOSDevice } from "@/utils/pwa";

export default function usePWAInstall() {
  const deferredPromptRef = useRef(null);
  const [hasPromptEvent, setHasPromptEvent] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isBrowserSupported, setIsBrowserSupported] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [installOutcome, setInstallOutcome] = useState(null);

  useEffect(() => {
    const installed = isPWAInstalled();
    const iosDevice = isIOSDevice();

    setIsInstalled(installed);
    setIsIOS(iosDevice);
    setIsBrowserSupported(iosDevice || "onbeforeinstallprompt" in window);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setHasPromptEvent(true);
      setIsBrowserSupported(true);
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      setHasPromptEvent(false);
      setIsInstalled(true);
      setInstallOutcome("accepted");
      setIsDismissed(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const resetDismissal = useCallback(() => {
    setIsDismissed(false);
  }, []);

  const promptInstall = useCallback(async () => {
    if (isIOS) {
      return { outcome: "ios-manual" };
    }

    const promptEvent = deferredPromptRef.current;

    if (!promptEvent || typeof promptEvent.prompt !== "function") {
      return { outcome: "unavailable" };
    }

    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;

    setInstallOutcome(choice.outcome);
    deferredPromptRef.current = null;
    setHasPromptEvent(false);

    if (choice.outcome === "accepted") {
      setIsInstalled(true);
    } else {
      setIsDismissed(true);
    }

    return choice;
  }, [isIOS]);

  const canInstall = useMemo(() => {
    if (isInstalled || isDismissed) {
      return false;
    }

    if (isIOS) {
      return true;
    }

    return hasPromptEvent;
  }, [hasPromptEvent, isDismissed, isIOS, isInstalled]);

  return {
    canInstall,
    dismiss,
    hasPromptEvent,
    installOutcome,
    isBrowserSupported,
    isDismissed,
    isIOS,
    isInstalled,
    promptInstall,
    resetDismissal,
  };
}
